import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { 
  Calendar, Clock, ArrowLeft, Tag, Share2, 
  Facebook, Twitter, Linkedin, Copy, Check,
  ChevronRight, BookOpen
} from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_URL}/api/blog/articles/${slug}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError("Article non trouvé");
          } else {
            setError("Erreur lors du chargement");
          }
          return;
        }
        const data = await response.json();
        if (data.success) {
          setArticle(data.article);
          setRelated(data.related || []);
        }
      } catch (err) {
        console.error("Error fetching article:", err);
        setError("Impossible de charger l'article");
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchArticle();
      window.scrollTo(0, 0);
    }
  }, [slug]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  const estimateReadTime = (content) => {
    if (!content) return "2 min";
    const text = content.replace(/<[^>]*>/g, '');
    const words = text.split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min`;
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleShare = async (platform) => {
    const title = article?.title || '';
    const url = shareUrl;

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'copy':
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          console.error('Failed to copy:', err);
        }
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navigation />
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <h1 className="text-2xl font-bold text-white mb-4">
            {error || "Article non trouvé"}
          </h1>
          <Link
            to="/blog"
            className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>{article.meta_title || article.title} | Blog Académie IA</title>
        <meta name="description" content={article.meta_description || article.excerpt} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.meta_description || article.excerpt} />
        <meta property="og:url" content={`https://academie-ia.fr/blog/${slug}`} />
        {article.featured_image && <meta property="og:image" content={article.featured_image} />}
        <meta property="og:site_name" content="Académie IA" />
        <meta property="article:published_time" content={article.published_at} />
        {article.author && <meta property="article:author" content={article.author} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.meta_description || article.excerpt} />
        {article.featured_image && <meta name="twitter:image" content={article.featured_image} />}
        
        {/* SEO */}
        <link rel="canonical" href={`https://academie-ia.fr/blog/${slug}`} />
        {article.tags && article.tags.length > 0 && (
          <meta name="keywords" content={article.tags.join(', ')} />
        )}
        
        {/* Structured Data - Article */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.meta_description || article.excerpt,
            "image": article.featured_image,
            "author": {
              "@type": "Organization",
              "name": article.author || "Académie IA"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Académie IA",
              "logo": {
                "@type": "ImageObject",
                "url": "https://academie-ia.fr/logo.png"
              }
            },
            "datePublished": article.published_at,
            "dateModified": article.updated_at || article.published_at,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://academie-ia.fr/blog/${slug}`
            }
          })}
        </script>
      </Helmet>

      <Navigation />

      <article className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-8">
            <Link to="/" className="hover:text-cyan-400 transition-colors">Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/blog" className="hover:text-cyan-400 transition-colors">Blog</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-500 truncate max-w-[200px]">{article.title}</span>
          </nav>

          {/* Article Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            {/* Category & Tags */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {article.category && (
                <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm font-medium">
                  {article.category}
                </span>
              )}
              {article.tags?.slice(0, 3).map((tag, index) => (
                <span 
                  key={index}
                  className="flex items-center gap-1 px-2 py-1 bg-white/5 text-gray-400 rounded text-xs"
                >
                  <Tag className="w-3 h-3" />
                  {tag}
                </span>
              ))}
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {article.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-gray-400 text-sm mb-6">
              {article.author && (
                <span className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-bold text-xs">
                    {article.author.charAt(0)}
                  </div>
                  {article.author}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(article.published_at)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {estimateReadTime(article.content)} de lecture
              </span>
            </div>

            {/* Excerpt */}
            {article.excerpt && (
              <p className="text-lg text-gray-300 leading-relaxed border-l-4 border-cyan-400 pl-4">
                {article.excerpt}
              </p>
            )}
          </motion.header>

          {/* Featured Image */}
          {article.featured_image && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-10"
            >
              <img
                src={article.featured_image}
                alt={article.title}
                className="w-full h-auto rounded-2xl shadow-2xl shadow-cyan-500/10"
              />
            </motion.div>
          )}

          {/* Article Content - Optimized Typography & SEO */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="article-content prose prose-invert prose-lg max-w-none mb-12"
            style={{
              fontFamily: "'Georgia', 'Times New Roman', serif",
              lineHeight: '1.9',
              fontSize: '1.125rem',
              letterSpacing: '0.01em'
            }}
          >
            <style>{`
              .article-content h1 {
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                font-size: 2.5rem;
                font-weight: 800;
                color: white;
                margin-top: 3rem;
                margin-bottom: 1.5rem;
                line-height: 1.2;
                letter-spacing: -0.02em;
              }
              .article-content h2 {
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                font-size: 1.875rem;
                font-weight: 700;
                color: white;
                margin-top: 3rem;
                margin-bottom: 1.25rem;
                line-height: 1.3;
                letter-spacing: -0.01em;
                padding-bottom: 0.5rem;
                border-bottom: 2px solid rgba(6, 182, 212, 0.3);
              }
              .article-content h3 {
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                font-size: 1.5rem;
                font-weight: 600;
                color: white;
                margin-top: 2.5rem;
                margin-bottom: 1rem;
                line-height: 1.4;
              }
              .article-content h4 {
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                font-size: 1.25rem;
                font-weight: 600;
                color: #e5e7eb;
                margin-top: 2rem;
                margin-bottom: 0.75rem;
              }
              .article-content p {
                color: #d1d5db;
                margin-bottom: 1.75rem;
                text-align: justify;
                hyphens: auto;
              }
              .article-content p:first-of-type {
                font-size: 1.25rem;
                color: #e5e7eb;
                line-height: 1.8;
              }
              .article-content a {
                color: #22d3ee;
                text-decoration: none;
                border-bottom: 1px solid rgba(34, 211, 238, 0.3);
                transition: all 0.2s;
              }
              .article-content a:hover {
                color: #67e8f9;
                border-bottom-color: #67e8f9;
              }
              .article-content strong, .article-content b {
                color: white;
                font-weight: 600;
              }
              .article-content ul, .article-content ol {
                margin: 1.5rem 0;
                padding-left: 1.5rem;
                color: #d1d5db;
              }
              .article-content li {
                margin-bottom: 0.75rem;
                padding-left: 0.5rem;
              }
              .article-content li::marker {
                color: #22d3ee;
              }
              .article-content blockquote {
                border-left: 4px solid #22d3ee;
                padding: 1.5rem 2rem;
                margin: 2rem 0;
                background: rgba(6, 182, 212, 0.05);
                border-radius: 0 0.75rem 0.75rem 0;
                font-style: italic;
                color: #9ca3af;
              }
              .article-content blockquote p {
                margin-bottom: 0;
              }
              .article-content code {
                background: rgba(255, 255, 255, 0.1);
                color: #67e8f9;
                padding: 0.2rem 0.4rem;
                border-radius: 0.25rem;
                font-size: 0.9em;
                font-family: 'Fira Code', 'Monaco', monospace;
              }
              .article-content pre {
                background: #111827;
                border: 1px solid rgba(255, 255, 255, 0.1);
                border-radius: 0.75rem;
                padding: 1.5rem;
                overflow-x: auto;
                margin: 2rem 0;
              }
              .article-content pre code {
                background: none;
                padding: 0;
              }
              .article-content img {
                border-radius: 1rem;
                margin: 2rem auto;
                box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
              }
              .article-content hr {
                border: none;
                height: 1px;
                background: linear-gradient(90deg, transparent, rgba(6, 182, 212, 0.5), transparent);
                margin: 3rem 0;
              }
              .article-content table {
                width: 100%;
                border-collapse: collapse;
                margin: 2rem 0;
              }
              .article-content th, .article-content td {
                border: 1px solid rgba(255, 255, 255, 0.1);
                padding: 0.75rem 1rem;
                text-align: left;
              }
              .article-content th {
                background: rgba(6, 182, 212, 0.1);
                color: white;
                font-weight: 600;
              }
              .article-content td {
                color: #d1d5db;
              }
              @media (max-width: 768px) {
                .article-content h1 { font-size: 2rem; }
                .article-content h2 { font-size: 1.5rem; }
                .article-content h3 { font-size: 1.25rem; }
                .article-content p { text-align: left; font-size: 1rem; }
                .article-content p:first-of-type { font-size: 1.1rem; }
              }
            `}</style>
            <div dangerouslySetInnerHTML={{ __html: article.content }} />
          </motion.div>

          {/* Share Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-between gap-4 py-6 border-t border-b border-white/10 mb-12"
          >
            <span className="flex items-center gap-2 text-gray-400">
              <Share2 className="w-4 h-4" />
              Partager cet article
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleShare('facebook')}
                className="p-3 bg-white/5 hover:bg-blue-600/20 rounded-full transition-colors"
                aria-label="Partager sur Facebook"
              >
                <Facebook className="w-5 h-5 text-blue-400" />
              </button>
              <button
                onClick={() => handleShare('twitter')}
                className="p-3 bg-white/5 hover:bg-sky-500/20 rounded-full transition-colors"
                aria-label="Partager sur Twitter"
              >
                <Twitter className="w-5 h-5 text-sky-400" />
              </button>
              <button
                onClick={() => handleShare('linkedin')}
                className="p-3 bg-white/5 hover:bg-blue-700/20 rounded-full transition-colors"
                aria-label="Partager sur LinkedIn"
              >
                <Linkedin className="w-5 h-5 text-blue-500" />
              </button>
              <button
                onClick={() => handleShare('copy')}
                className="p-3 bg-white/5 hover:bg-cyan-500/20 rounded-full transition-colors"
                aria-label="Copier le lien"
              >
                {copied ? (
                  <Check className="w-5 h-5 text-green-400" />
                ) : (
                  <Copy className="w-5 h-5 text-gray-400" />
                )}
              </button>
            </div>
          </motion.div>

          {/* Related Articles */}
          {related.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <BookOpen className="w-6 h-6 text-cyan-400" />
                Articles similaires
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {related.map((relatedArticle) => (
                  <Link
                    key={relatedArticle.slug}
                    to={`/blog/${relatedArticle.slug}`}
                    className="group block p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-cyan-400/30 rounded-xl transition-all"
                  >
                    {relatedArticle.featured_image && (
                      <img
                        src={relatedArticle.featured_image}
                        alt={relatedArticle.title}
                        className="w-full h-32 object-cover rounded-lg mb-3"
                      />
                    )}
                    <h3 className="text-white font-semibold group-hover:text-cyan-400 transition-colors line-clamp-2 mb-2">
                      {relatedArticle.title}
                    </h3>
                    <p className="text-gray-400 text-sm line-clamp-2">
                      {relatedArticle.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            </motion.section>
          )}

          {/* Back to Blog */}
          <div className="mt-12 text-center">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded-full transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour au blog
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogArticlePage;
